import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EnvelopeSimpleIcon,
  LinkSimpleIcon,
  ChecksIcon
} from "@phosphor-icons/react";

import Icon from "components/Icon/Icon";
import Input from "components/Input/index";
import Select from "components/Select/index";
import Button from "components/Buttons/index";

import { useMarketplaceSlice } from "pages/Marketplace/slice/index";

import { selectMarketplaceUser } from "pages/Marketplace/slice/selector";

import { DetailsWrapperStyles } from "../styled";
import { ValidationWrapperStyles } from "styles/GlobalStyles";
import { LINKEDIN_REGEX } from "utils/helper";

const UserDetails = ({
  value = {},
  onChange,
  companies,
  user,
  setHasCheckedUser,
  setHasCheckedEmail,
  validationLoading,
  validateAction,
  success,
  error
}) => {
  const dispatch = useDispatch();
  const { actions } = useMarketplaceSlice();
  const { loading } = useSelector(selectMarketplaceUser);

  const [isEditable, setIsEditable] = useState(false);
  const [linkedinError, setLinkedinError] = useState("");

  /* ---------- Company options ---------- */
  const companyOptions = useMemo(() => {
    return (
      companies?.map((company) => ({
        value: company.id,
        label: company.company_name?.trim()
      })) || []
    );
  }, [companies]);

  // useEffect(() => {
  //   return () => {
  //     // Reset validation when leaving the component
  //     dispatch(validateAction.resetEmailValidation());
  //     setHasCheckedEmail(false);
  //   };
  // }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Revoke previous preview if exists
    if (value.logoPreview) URL.revokeObjectURL(value.logoPreview);

    // Generate preview URL
    const preview = URL.createObjectURL(file);

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Data = reader.result.split(",")[1]; // remove data:*/*;base64,

      updateField("user_profile_image", {
        base64Data,
        name: file.name,
        sizeBytes: file.size,
        type: file.type
      });

      updateField("logoPreview", preview); // for preview
      updateField("logoName", file.name); // optional
    };

    reader.readAsDataURL(file); // triggers onloadend
  };

  /* ---------- Prefill logic ---------- */
  useEffect(() => {
    if (!user || !companyOptions.length) return;

    // EXISTING USER
    if (user.user_id !== -1) {
      const selectedCompany = companyOptions.find(
        (opt) => opt.value === user.company_id
      );

      setIsEditable(false);

      onChange((prev) => ({
        ...prev,
        user_id_hidden: user.user_id,
        first_name: prev.first_name ?? user.first_name ?? "",
        last_name: prev.last_name ?? user.last_name ?? "",
        email: prev.email ?? user.email ?? "",
        linkedin_url: prev.linkedin_url ?? user.linkedin_url ?? "",
        user_designation: prev.user_designation ?? user.user_designation ?? "",
        company: prev.company ?? selectedCompany ?? null,
        // ADD THESE 3
        user_profile_image: user.user_profile_image, // backend value
        logoPreview: user.user_profile_image, // image URL
        logoName: user.user_profile_image ? user.user_profile_image : ""
      }));
    }

    // NEW USER
    if (user.user_id === -1) {
      setIsEditable(true);

      onChange((prev) => ({
        ...prev,
        user_id_hidden: -1
      }));
    }
  }, [user, companyOptions]);

  /* ---------- Helpers ---------- */
  const updateField = (key, val) => {
    onChange((prev) => ({
      ...prev,
      [key]: val
    }));
  };

  /* ---------- Phone number change handler ---------- */
  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");

    if (val.length <= 10) {
      onChange(() => ({
        user_phone_number: val
      }));

      if (val.length < 10) {
        setHasCheckedUser(false);
        setHasCheckedEmail(false);
        setIsEditable(false);

        dispatch(validateAction.resetEmailValidation());
      }
    }
  };

  const handleCheckUser = () => {
    if (!value.user_phone_number) return;

    setIsEditable(false);

    dispatch(
      actions.fetchMarketplaceUser({
        user_phone_number: value.user_phone_number
      })
    );

    setHasCheckedUser(true);
  };

  const handleValidateEmail = () => {
    if (!value?.email) return;

    dispatch(
      validateAction.validateEmail({
        email: value.email
      })
    );

    setHasCheckedEmail(true);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;

    updateField("email", email);

    // Reset validation state when email changes
    setHasCheckedEmail(false);

    if (success || error) {
      dispatch(validateAction.resetEmailValidation());
    }
  };

  return (
    <DetailsWrapperStyles>
      <div className="left-side-wrapper">
        <div className="phone-number-wrapper">
          <Input
            id="phone"
            type="tel"
            label="Phone Number"
            placeholder="Enter phone number"
            required
            value={value?.user_phone_number || ""}
            onChange={handlePhoneChange}
          />

          <Button
            onClick={handleCheckUser}
            disabled={
              !value?.user_phone_number ||
              value?.user_phone_number.length !== 10 ||
              loading
            }
          >
            {loading ? "Check..." : "Check"}
          </Button>
        </div>

        <Input
          id="first_name"
          label="First Name"
          required
          placeholder="Enter First Name"
          value={value?.first_name || ""}
          onChange={(e) => updateField("first_name", e.target.value)}
          disabled={!isEditable}
        />

        <Input
          id="last_name"
          label="Last Name"
          required
          placeholder="Enter Last Name"
          value={value?.last_name || ""}
          onChange={(e) => updateField("last_name", e.target.value)}
          disabled={!isEditable}
        />

        {/* <Select
          label="Company"
          placeholder="Select Company"
          required
          options={companyOptions}
          value={value?.company ?? undefined}
          onChange={(val) => updateField("company", val)} // <- val is full object
          disabled={!isEditable}
        /> */}

        <Input
          id="user_designation"
          label="Designation"
          placeholder="Enter Designation"
          required
          value={value?.user_designation || ""}
          onChange={(e) => updateField("user_designation", e.target.value)}
          disabled={!isEditable}
        />
      </div>

      <div className="right-side-wrapper">
        <ValidationWrapperStyles className={error ? "err-msg" : ""}>
          <Input
            label="Email"
            required
            type="mail"
            id="email"
            placeholder="Enter Email"
            leftIcon={<Icon component={EnvelopeSimpleIcon} color="#A5A5A5" />}
            value={value?.email || ""}
            onChange={handleEmailChange}
            disabled={!isEditable}
            hasError={error}
            helperText={error}
          />
          <Button
            onClick={handleValidateEmail}
            disabled={
              !value?.email || validationLoading || user?.user_id !== -1
            }
            data-success={success}
          >
            {validationLoading && "Checking..."}

            {!validationLoading && !success && "Check"}

            {!validationLoading && success && (
              <>
                <Icon component={ChecksIcon} color="#3F9142" />
                Checked
              </>
            )}
          </Button>
        </ValidationWrapperStyles>
        <Input
          label="LinkedIn URL"
          required
          placeholder="Enter LinkedIn Link"
          type="text"
          id="linkedin"
          leftIcon={<Icon component={LinkSimpleIcon} color="#A5A5A5" />}
          value={value?.linkedin_url || ""}
          hasError={!!linkedinError}
          helperText={linkedinError}
          onChange={(e) => {
            const val = e.target.value;
            updateField("linkedin_url", val);

            if (!val) {
              setLinkedinError("");
              return;
            }

            if (!LINKEDIN_REGEX.test(val)) {
              setLinkedinError("Please enter a valid LinkedIn URL");
            } else {
              setLinkedinError("");
            }
          }}
          disabled={!isEditable}
        />

        <div className="input-wrappers">
          <Input
            id="user_profile_image"
            label="Profile Image"
            readOnly
            placeholder="Browse File"
            value={value?.logoName}
            size="M"
            leftClass="logo-preview-wrapper"
            leftIcon={
              value.logoPreview ? (
                <img src={value?.logoPreview} alt="logo-preview" />
              ) : null
            }
            disabled={!isEditable}
            required
          />

          <input
            type="file"
            id="file-input"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={!isEditable}
          />

          {!user?.user_profile_image && (
            <Button
              size="M"
              variant="primary"
              onClick={() => document.getElementById("file-input").click()}
              disabled={!isEditable}
            >
              {value.logoPreview ? "Re-Upload" : "Browse File"}
            </Button>
          )}
        </div>
      </div>
    </DetailsWrapperStyles>
  );
};

export default UserDetails;
